import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type {
  Json,
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@/lib/database.types";
import { useAuthStore } from "@/store/authStore";
import type {
  BuilderMode,
  BuilderDocumentSettings,
  ClientType,
  FixedPagePolicy,
  PriceUnit,
  ProposalDocumentLayout,
  ProposalTemplate,
  ProposalTemplateInsert,
  ProposalTemplateUpdate,
  TemplateLineItem,
  TemplatePage,
  TemplateService,
  TemplateStaticPage,
} from "../types";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const CLIENT_TYPES: ClientType[] = [
  "construction",
  "restaurant",
  "commercial",
  "residential",
  "other",
];

const PRICE_UNITS: PriceUnit[] = [
  "per_clean",
  "per_hour",
  "per_sqft",
  "per_visit",
  "fixed",
];

const BUILDER_MODES: BuilderMode[] = ["prebuilt", "drag_drop"];
const FIXED_PAGE_POLICIES: FixedPagePolicy[] = [
  "template_defined",
  "brand_defined",
  "mixed",
];

function isUuid(value: string | undefined | null): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getString(
  source: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function getNumber(
  source: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

function getBoolean(
  source: Record<string, unknown>,
  ...keys: string[]
): boolean | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") {
      return value;
    }
  }
  return null;
}

function getArray(
  source: Record<string, unknown>,
  ...keys: string[]
): unknown[] {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function getObject(
  source: Record<string, unknown>,
  ...keys: string[]
): Record<string, unknown> | null {
  for (const key of keys) {
    const value = source[key];
    if (isRecord(value)) {
      return value;
    }
  }
  return null;
}

function toJsonValue(value: unknown): Json {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => toJsonValue(item));
  }

  if (isRecord(value)) {
    const output: { [key: string]: Json | undefined } = {};
    for (const [key, entry] of Object.entries(value)) {
      output[key] = toJsonValue(entry);
    }
    return output;
  }

  return null;
}

function toJsonObject(value: unknown): { [key: string]: Json | undefined } {
  if (!isRecord(value)) {
    return {};
  }

  const output: { [key: string]: Json | undefined } = {};
  for (const [key, entry] of Object.entries(value)) {
    output[key] = toJsonValue(entry);
  }
  return output;
}

function toClientType(value: string | null): ClientType | null {
  if (!value) return null;
  return CLIENT_TYPES.includes(value as ClientType)
    ? (value as ClientType)
    : "other";
}

function toPriceUnit(value: string | null | undefined): PriceUnit | null {
  if (!value) return null;
  return PRICE_UNITS.includes(value as PriceUnit)
    ? (value as PriceUnit)
    : null;
}

function toBuilderMode(value: string | null): BuilderMode {
  if (!value) return "prebuilt";
  return BUILDER_MODES.includes(value as BuilderMode)
    ? (value as BuilderMode)
    : "prebuilt";
}

function toFixedPagePolicy(value: string | null): FixedPagePolicy {
  if (!value) return "template_defined";
  return FIXED_PAGE_POLICIES.includes(value as FixedPagePolicy)
    ? (value as FixedPagePolicy)
    : "template_defined";
}

function toDocumentSettings(value: unknown): BuilderDocumentSettings {
  if (!isRecord(value)) {
    return {};
  }
  return value as BuilderDocumentSettings;
}

function toLayoutJson(value: unknown): ProposalDocumentLayout {
  if (!isRecord(value)) {
    return { pages: [] };
  }

  const pages = Array.isArray(value.pages) ? value.pages : [];
  return {
    ...(value as ProposalDocumentLayout),
    pages,
  };
}

type ProposalTemplateRow = Tables<"proposal_templates">;
type TemplateServiceRow = Tables<"template_services">;
type TemplateStaticPageRow = Tables<"template_static_pages">;
type TemplatePageRow = Tables<"template_pages">;
type ProposalLineItemRow = Tables<"proposal_line_items">;
type ProposalStaticPageRow = Tables<"proposal_static_pages">;
type StaticPageRow = Tables<"static_pages">;

type TemplateWithRelations = ProposalTemplateRow & {
  template_services?: TemplateServiceRow[] | null;
  template_static_pages?: TemplateStaticPageRow[] | null;
  template_pages?: TemplatePageRow[] | null;
};

type TemplateServiceInsertRow = TablesInsert<"template_services">;
type TemplateStaticPageInsertRow = TablesInsert<"template_static_pages">;

type TemplatePayloadRecord = Record<string, unknown>;

function normalizeTemplateClientType(
  payload: TemplatePayloadRecord
): ClientType | null {
  const explicitClientType = toClientType(getString(payload, "client_type"));
  if (explicitClientType) return explicitClientType;

  const categoryAsClientType = toClientType(getString(payload, "category"));
  if (categoryAsClientType) return categoryAsClientType;

  return null;
}

function normalizeTemplateServices(
  payload: TemplatePayloadRecord,
  templateId: string
): TemplateServiceInsertRow[] {
  const rawServices = getArray(
    payload,
    "template_services",
    "services",
    "line_items",
    "default_line_items"
  );

  return rawServices
    .map((item, index): TemplateServiceInsertRow | null => {
      if (!isRecord(item)) return null;

      const serviceName = getString(item, "service_name", "name");
      if (!serviceName) return null;

      const serviceItemId = getString(item, "service_item_id");
      const quantity = getNumber(item, "quantity");
      const sortOrder = getNumber(item, "sort_order");

      return {
        template_id: templateId,
        service_item_id: isUuid(serviceItemId) ? serviceItemId : null,
        service_name: serviceName,
        service_description: getString(
          item,
          "service_description",
          "description"
        ),
        base_price: getNumber(item, "base_price", "price"),
        price_unit: toPriceUnit(getString(item, "price_unit")) ?? "per_clean",
        quantity: quantity ?? 1,
        sort_order: sortOrder ?? index,
      };
    })
    .filter((item): item is TemplateServiceInsertRow => item !== null);
}

async function normalizeTemplateStaticPages(
  payload: TemplatePayloadRecord,
  templateId: string
): Promise<TemplateStaticPageInsertRow[]> {
  const rawPages = getArray(
    payload,
    "template_static_pages",
    "static_pages",
    "default_static_pages"
  );

  if (rawPages.length > 0) {
    return rawPages
      .map((item, index): TemplateStaticPageInsertRow | null => {
        if (!isRecord(item)) return null;

        const staticPageId = getString(item, "static_page_id", "id");
        const title = getString(item, "title");
        if (!title) return null;

        const pageOrder = getNumber(item, "page_order");

        return {
          template_id: templateId,
          static_page_id: isUuid(staticPageId) ? staticPageId : null,
          title,
          content_html: getString(item, "content_html", "content"),
          page_order: pageOrder ?? index,
        };
      })
      .filter((item): item is TemplateStaticPageInsertRow => item !== null);
  }

  const rawIds = getArray(payload, "default_static_page_ids")
    .map((value) => (typeof value === "string" ? value : null))
    .filter((value): value is string => isUuid(value));

  if (rawIds.length === 0) return [];

  const { data, error } = await supabase
    .from("static_pages")
    .select("*")
    .in("id", rawIds);

  if (error) throw error;

  const pageMap = new Map<string, StaticPageRow>();
  for (const page of data ?? []) {
    pageMap.set(page.id, page);
  }

  return rawIds
    .map((pageId, index): TemplateStaticPageInsertRow | null => {
      const page = pageMap.get(pageId);
      if (!page) return null;

      return {
        template_id: templateId,
        static_page_id: page.id,
        title: page.title,
        content_html: page.content_html,
        page_order: page.page_order ?? index,
      };
    })
    .filter((item): item is TemplateStaticPageInsertRow => item !== null);
}

function buildLegacyDefaultLineItems(
  services: TemplateServiceInsertRow[]
): TemplateLineItem[] {
  return services.map((service) => ({
    service_item_id: service.service_item_id ?? null,
    name: service.service_name,
    description: service.service_description ?? null,
    price: service.base_price ?? null,
    price_unit: toPriceUnit(service.price_unit),
    frequency: null,
    duration_hours: null,
    sort_order: service.sort_order ?? 0,
  }));
}

function buildLegacyDefaultStaticPageIds(
  pages: TemplateStaticPageInsertRow[]
): string[] {
  return pages
    .map((page) => page.static_page_id)
    .filter((id): id is string => typeof id === "string" && isUuid(id));
}

function mapTemplateService(row: TemplateServiceRow): TemplateService {
  return {
    id: row.id,
    template_id: row.template_id,
    service_item_id: row.service_item_id,
    service_name: row.service_name,
    service_description: row.service_description,
    base_price: row.base_price,
    price_unit: toPriceUnit(row.price_unit),
    quantity: row.quantity ?? 1,
    sort_order: row.sort_order ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function mapTemplateStaticPage(
  row: TemplateStaticPageRow
): TemplateStaticPage {
  return {
    id: row.id,
    template_id: row.template_id,
    static_page_id: row.static_page_id,
    title: row.title,
    content_html: row.content_html,
    page_order: row.page_order ?? 0,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function mapTemplatePage(row: TemplatePageRow): TemplatePage {
  return {
    id: row.id,
    template_id: row.template_id,
    title: row.title,
    page_type: row.page_type as TemplatePage["page_type"],
    page_order: row.page_order ?? 0,
    source_static_page_id: row.source_static_page_id,
    is_required: row.is_required ?? false,
    is_locked: row.is_locked ?? false,
    include_in_preview: row.include_in_preview ?? true,
    settings:
      isRecord(row.settings) ? (row.settings as TemplatePage["settings"]) : {},
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function mapLegacyTemplateLineItems(value: unknown): TemplateLineItem[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item): TemplateLineItem | null => {
      if (!isRecord(item)) return null;

      const name = getString(item, "name");
      if (!name) return null;

      return {
        service_item_id: getString(item, "service_item_id"),
        name,
        description: getString(item, "description"),
        price: getNumber(item, "price"),
        price_unit: toPriceUnit(getString(item, "price_unit")),
        frequency: getString(item, "frequency"),
        duration_hours: getNumber(item, "duration_hours"),
        sort_order: getNumber(item, "sort_order") ?? 0,
      };
    })
    .filter((item): item is TemplateLineItem => item !== null);
}

function mapTemplateRecord(
  template: TemplateWithRelations | null
): ProposalTemplate | null {
  if (!template) return null;

  return {
    id: template.id,
    client_id: template.client_id,
    name: template.name,
    description: template.description,
    category: template.category,
    tax_rate: template.tax_rate,

    builder_mode: toBuilderMode(template.builder_mode),
    layout_version:
      typeof template.layout_version === "number" ? template.layout_version : 1,
    document_settings: toDocumentSettings(template.document_settings),
    layout_json: toLayoutJson(template.layout_json),
    preview_image_url: template.preview_image_url,
    fixed_page_policy: toFixedPagePolicy(template.fixed_page_policy),

    client_type: toClientType(template.client_type),
    ai_tone: template.ai_tone,
    ai_industry_context: template.ai_industry_context,
    ai_service_context: template.ai_service_context,
    default_line_items: mapLegacyTemplateLineItems(template.default_line_items),
    default_static_page_ids:
      Array.isArray(template.default_static_page_ids)
        ? template.default_static_page_ids.filter(
            (id): id is string => typeof id === "string"
          )
        : [],
    default_tax_rate: template.default_tax_rate ?? 0,
    source_proposal_id: template.source_proposal_id,
    is_default: template.is_default ?? false,
    created_by: template.created_by,
    created_at: template.created_at,
    updated_at: template.updated_at,
    template_services: (template.template_services ?? []).map(mapTemplateService),
    template_static_pages: (template.template_static_pages ?? []).map(
      mapTemplateStaticPage
    ),
    template_pages: (template.template_pages ?? []).map(mapTemplatePage),
  };
}

const templateListKey = (clientId: string | null) =>
  ["proposal-templates", clientId ?? "no-client", "list"] as const;

const templateDetailKey = (id: string | null) =>
  ["proposal-templates", "detail", id ?? "invalid"] as const;

const TEMPLATE_RELATION_SELECT = `
  *,
  template_services (*),
  template_static_pages (*),
  template_pages (*)
`;

export function useProposalTemplates() {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<ProposalTemplate[]>({
    queryKey: templateListKey(clientId),
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from("proposal_templates")
        .select(TEMPLATE_RELATION_SELECT)
        .eq("client_id", clientId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

      if (error) throw error;

      return ((data ?? []) as TemplateWithRelations[])
        .map((template) => mapTemplateRecord(template))
        .filter((template): template is ProposalTemplate => template !== null);
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

export function useProposalTemplate(id: string | undefined) {
  const validId = isUuid(id) ? id : null;

  return useQuery<ProposalTemplate | null>({
    queryKey: templateDetailKey(validId),
    queryFn: async () => {
      if (!validId) return null;

      const { data, error } = await supabase
        .from("proposal_templates")
        .select(TEMPLATE_RELATION_SELECT)
        .eq("id", validId)
        .single();

      if (error) throw error;
      return mapTemplateRecord((data ?? null) as TemplateWithRelations | null);
    },
    enabled: !!validId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

export function useCreateTemplate() {
  const clientId = useAuthStore((s) => s.clientId);
  const userId = useAuthStore((s) => s.userId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (insert: ProposalTemplateInsert | ProposalTemplateUpdate) => {
      if (!clientId) throw new Error("No client ID");

      const payload = insert as unknown as TemplatePayloadRecord;
      const isDefault = getBoolean(payload, "is_default") ?? false;

      if (isDefault) {
        const { error: unsetError } = await supabase
          .from("proposal_templates")
          .update({ is_default: false })
          .eq("client_id", clientId)
          .eq("is_default", true);

        if (unsetError) throw unsetError;
      }

      const sourceProposalId = getString(payload, "source_proposal_id");
      const normalizedClientType = normalizeTemplateClientType(payload);
      const builderMode = toBuilderMode(getString(payload, "builder_mode"));
      const layoutVersion = getNumber(payload, "layout_version") ?? 1;
      const documentSettings = toJsonObject(getObject(payload, "document_settings"));
      const layoutJson = toLayoutJson(payload.layout_json);
      const fixedPagePolicy = toFixedPagePolicy(
        getString(payload, "fixed_page_policy")
      );

      const { data: createdTemplate, error: createError } = await supabase
        .from("proposal_templates")
        .insert({
          client_id: clientId,
          created_by: userId ?? null,
          name: getString(payload, "name") ?? "Untitled template",
          description: getString(payload, "description"),
          category: getString(payload, "category"),
          tax_rate: getNumber(payload, "tax_rate", "default_tax_rate") ?? 0,
          client_type: normalizedClientType,
          is_default: isDefault,
          source_proposal_id: isUuid(sourceProposalId) ? sourceProposalId : null,
          ai_tone: getString(payload, "ai_tone"),
          ai_industry_context: getString(payload, "ai_industry_context"),
          ai_service_context: getString(payload, "ai_service_context"),
          builder_mode: builderMode,
          layout_version: layoutVersion,
          document_settings: documentSettings,
          layout_json: layoutJson,
          preview_image_url: getString(payload, "preview_image_url"),
          fixed_page_policy: fixedPagePolicy,
          default_line_items: [],
          default_static_page_ids: [],
          default_tax_rate:
            getNumber(payload, "default_tax_rate", "tax_rate") ?? 0,
        })
        .select()
        .single();

      if (createError) throw createError;

      const services = normalizeTemplateServices(payload, createdTemplate.id);
      const staticPages = await normalizeTemplateStaticPages(
        payload,
        createdTemplate.id
      );

      const legacyLineItems = buildLegacyDefaultLineItems(services);
      const legacyStaticPageIds = buildLegacyDefaultStaticPageIds(staticPages);

      const { error: syncLegacyError } = await supabase
        .from("proposal_templates")
        .update({
          default_line_items: legacyLineItems,
          default_static_page_ids: legacyStaticPageIds,
          updated_at: new Date().toISOString(),
        })
        .eq("id", createdTemplate.id)
        .eq("client_id", clientId);

      if (syncLegacyError) throw syncLegacyError;

      if (services.length > 0) {
        const { error: servicesError } = await supabase
          .from("template_services")
          .insert(services);

        if (servicesError) throw servicesError;
      }

      if (staticPages.length > 0) {
        const { error: staticPagesError } = await supabase
          .from("template_static_pages")
          .insert(staticPages);

        if (staticPagesError) throw staticPagesError;
      }

      const { data: hydratedTemplate, error: reloadError } = await supabase
        .from("proposal_templates")
        .select(TEMPLATE_RELATION_SELECT)
        .eq("id", createdTemplate.id)
        .single();

      if (reloadError) throw reloadError;

      return mapTemplateRecord(hydratedTemplate as TemplateWithRelations)!;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: templateListKey(clientId) });
      qc.setQueryData(templateDetailKey(data.id), data);
    },
  });
}

export function useCreateTemplateFromProposal() {
  const clientId = useAuthStore((s) => s.clientId);
  const userId = useAuthStore((s) => s.userId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      proposalId,
      name,
      clientType,
      description,
      aiTone,
      aiIndustryContext,
      aiServiceContext,
    }: {
      proposalId: string;
      name: string;
      clientType: ClientType;
      description?: string;
      aiTone?: string;
      aiIndustryContext?: string;
      aiServiceContext?: string;
    }) => {
      if (!clientId) throw new Error("No client ID");
      if (!isUuid(proposalId)) throw new Error("Invalid proposal ID");

      const { data: lineItems, error: lineItemsError } = await supabase
        .from("proposal_line_items")
        .select("*")
        .eq("proposal_id", proposalId)
        .order("sort_order");

      if (lineItemsError) throw lineItemsError;

      const { data: proposalPages, error: proposalPagesError } = await supabase
        .from("proposal_static_pages")
        .select("*")
        .eq("proposal_id", proposalId)
        .order("page_order");

      if (proposalPagesError) throw proposalPagesError;

      const staticPageIds = (proposalPages ?? [])
        .map((page: ProposalStaticPageRow) => page.static_page_id)
        .filter((pageId): pageId is string => isUuid(pageId));

      let staticPages: StaticPageRow[] = [];
      if (staticPageIds.length > 0) {
        const { data, error } = await supabase
          .from("static_pages")
          .select("*")
          .in("id", staticPageIds);

        if (error) throw error;
        staticPages = data ?? [];
      }

      const pageMap = new Map<string, StaticPageRow>();
      for (const page of staticPages) {
        pageMap.set(page.id, page);
      }

      const legacyLineItems: TemplateLineItem[] = (lineItems ?? []).map(
        (item: ProposalLineItemRow) => ({
          service_item_id: item.service_item_id,
          name: item.name,
          description: item.description ?? item.ai_generated_description ?? null,
          price: item.price,
          price_unit: toPriceUnit(item.price_unit),
          frequency: item.frequency,
          duration_hours: item.duration_hours,
          sort_order: item.sort_order ?? 0,
        })
      );

      const legacyStaticPageIds = staticPageIds;

      const derivedFixedPagePolicy: FixedPagePolicy =
        staticPages.some((page) => page.is_fixed)
          ? staticPages.some((page) => !page.is_fixed)
            ? "mixed"
            : "brand_defined"
          : "template_defined";

      const { data: createdTemplate, error: createError } = await supabase
        .from("proposal_templates")
        .insert({
          client_id: clientId,
          created_by: userId ?? null,
          name: name.trim(),
          description: description?.trim() || null,
          category: clientType,
          tax_rate: 0,
          client_type: clientType,
          builder_mode: "prebuilt",
          layout_version: 1,
          document_settings: {
            pageSize: "letter",
            pageOrientation: "portrait",
          },
          layout_json: { pages: [] },
          preview_image_url: null,
          fixed_page_policy: derivedFixedPagePolicy,
          is_default: false,
          source_proposal_id: proposalId,
          ai_tone: aiTone?.trim() || null,
          ai_industry_context: aiIndustryContext?.trim() || null,
          ai_service_context: aiServiceContext?.trim() || null,
          default_line_items: legacyLineItems,
          default_static_page_ids: legacyStaticPageIds,
          default_tax_rate: 0,
        })
        .select()
        .single();

      if (createError) throw createError;

      const serviceInserts: TemplateServiceInsertRow[] = (lineItems ?? []).map(
        (item: ProposalLineItemRow) => ({
          template_id: createdTemplate.id,
          service_item_id: item.service_item_id,
          service_name: item.name,
          service_description:
            item.description ?? item.ai_generated_description ?? null,
          base_price: item.price,
          price_unit: toPriceUnit(item.price_unit) ?? "per_clean",
          quantity: item.quantity ?? 1,
          sort_order: item.sort_order ?? 0,
        })
      );

      if (serviceInserts.length > 0) {
        const { error: servicesError } = await supabase
          .from("template_services")
          .insert(serviceInserts);

        if (servicesError) throw servicesError;
      }

      const pageInserts = (proposalPages ?? []).reduce<TemplateStaticPageInsertRow[]>(
        (acc, item: ProposalStaticPageRow, index) => {
          const staticPageId = item.static_page_id;
          if (!isUuid(staticPageId)) return acc;

          const page = pageMap.get(staticPageId);
          if (!page) return acc;

          acc.push({
            template_id: createdTemplate.id,
            static_page_id: page.id,
            title: page.title,
            content_html: page.content_html,
            page_order: item.page_order ?? index,
          });

          return acc;
        },
        []
      );

      if (pageInserts.length > 0) {
        const { error: pagesError } = await supabase
          .from("template_static_pages")
          .insert(pageInserts);

        if (pagesError) throw pagesError;
      }

      const { data: hydratedTemplate, error: reloadError } = await supabase
        .from("proposal_templates")
        .select(TEMPLATE_RELATION_SELECT)
        .eq("id", createdTemplate.id)
        .single();

      if (reloadError) throw reloadError;

      return mapTemplateRecord(hydratedTemplate as TemplateWithRelations)!;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: templateListKey(clientId) });
      qc.setQueryData(templateDetailKey(data.id), data);
    },
  });
}

export function useUpdateTemplate() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: ProposalTemplateUpdate;
    }) => {
      if (!clientId) throw new Error("No client ID");
      if (!isUuid(id)) throw new Error("Invalid template ID");

      const payload = updates as unknown as TemplatePayloadRecord;
      const isDefault = getBoolean(payload, "is_default");

      if (isDefault) {
        const { error: unsetError } = await supabase
          .from("proposal_templates")
          .update({ is_default: false })
          .eq("client_id", clientId)
          .eq("is_default", true)
          .neq("id", id);

        if (unsetError) throw unsetError;
      }

      const parentUpdate: TablesUpdate<"proposal_templates"> = {
        updated_at: new Date().toISOString(),
      };

      const name = getString(payload, "name");
      if (name) parentUpdate.name = name;

      if ("description" in payload) {
        parentUpdate.description = getString(payload, "description");
      }

      if ("category" in payload) {
        parentUpdate.category = getString(payload, "category");
      }

      if ("tax_rate" in payload || "default_tax_rate" in payload) {
        parentUpdate.tax_rate =
          getNumber(payload, "tax_rate", "default_tax_rate") ?? 0;
        parentUpdate.default_tax_rate =
          getNumber(payload, "default_tax_rate", "tax_rate") ?? 0;
      }

      if ("is_default" in payload && isDefault !== null) {
        parentUpdate.is_default = isDefault;
      }

      if ("client_type" in payload || "category" in payload) {
        parentUpdate.client_type =
          normalizeTemplateClientType(payload) ?? undefined;
      }

      if ("builder_mode" in payload) {
        parentUpdate.builder_mode = toBuilderMode(getString(payload, "builder_mode"));
      }

      if ("layout_version" in payload) {
        parentUpdate.layout_version = getNumber(payload, "layout_version") ?? 1;
      }

      if ("document_settings" in payload) {
        parentUpdate.document_settings = toJsonObject(
          getObject(payload, "document_settings")
        );
      }

      if ("layout_json" in payload) {
        parentUpdate.layout_json = toLayoutJson(payload.layout_json);
      }

      if ("preview_image_url" in payload) {
        parentUpdate.preview_image_url = getString(payload, "preview_image_url");
      }

      if ("fixed_page_policy" in payload) {
        parentUpdate.fixed_page_policy = toFixedPagePolicy(
          getString(payload, "fixed_page_policy")
        );
      }

      if ("source_proposal_id" in payload) {
        const sourceProposalId = getString(payload, "source_proposal_id");
        parentUpdate.source_proposal_id = isUuid(sourceProposalId)
          ? sourceProposalId
          : null;
      }

      if ("ai_tone" in payload) {
        parentUpdate.ai_tone = getString(payload, "ai_tone");
      }

      if ("ai_industry_context" in payload) {
        parentUpdate.ai_industry_context = getString(
          payload,
          "ai_industry_context"
        );
      }

      if ("ai_service_context" in payload) {
        parentUpdate.ai_service_context = getString(
          payload,
          "ai_service_context"
        );
      }

      const hasServicePayload =
        Array.isArray((payload as Record<string, unknown>).template_services) ||
        Array.isArray((payload as Record<string, unknown>).services) ||
        Array.isArray((payload as Record<string, unknown>).line_items) ||
        Array.isArray((payload as Record<string, unknown>).default_line_items);

      const hasPagePayload =
        Array.isArray((payload as Record<string, unknown>).template_static_pages) ||
        Array.isArray((payload as Record<string, unknown>).static_pages) ||
        Array.isArray((payload as Record<string, unknown>).default_static_pages) ||
        Array.isArray(
          (payload as Record<string, unknown>).default_static_page_ids
        );

      let nextLegacyLineItems: TemplateLineItem[] | undefined;
      let nextLegacyStaticPageIds: string[] | undefined;

      if (hasServicePayload) {
        const normalizedServices = normalizeTemplateServices(payload, id);
        nextLegacyLineItems = buildLegacyDefaultLineItems(normalizedServices);

        const { error: deleteServicesError } = await supabase
          .from("template_services")
          .delete()
          .eq("template_id", id);

        if (deleteServicesError) throw deleteServicesError;

        if (normalizedServices.length > 0) {
          const { error: insertServicesError } = await supabase
            .from("template_services")
            .insert(normalizedServices);

          if (insertServicesError) throw insertServicesError;
        }
      }

      if (hasPagePayload) {
        const normalizedPages = await normalizeTemplateStaticPages(payload, id);
        nextLegacyStaticPageIds =
          buildLegacyDefaultStaticPageIds(normalizedPages);

        const { error: deletePagesError } = await supabase
          .from("template_static_pages")
          .delete()
          .eq("template_id", id);

        if (deletePagesError) throw deletePagesError;

        if (normalizedPages.length > 0) {
          const { error: insertPagesError } = await supabase
            .from("template_static_pages")
            .insert(normalizedPages);

          if (insertPagesError) throw insertPagesError;
        }
      }

      if (hasServicePayload || hasPagePayload) {
        const compatibilityUpdate: TablesUpdate<"proposal_templates"> = {
          updated_at: new Date().toISOString(),
        };

        if (nextLegacyLineItems) {
          compatibilityUpdate.default_line_items = nextLegacyLineItems;
        }

        if (nextLegacyStaticPageIds) {
          compatibilityUpdate.default_static_page_ids = nextLegacyStaticPageIds;
        }

        const { error: syncLegacyError } = await supabase
          .from("proposal_templates")
          .update(compatibilityUpdate)
          .eq("id", id)
          .eq("client_id", clientId);

        if (syncLegacyError) throw syncLegacyError;
      }

      const { data: updatedTemplate, error: updateError } = await supabase
        .from("proposal_templates")
        .update(parentUpdate)
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (updateError) throw updateError;

      const { data: hydratedTemplate, error: reloadError } = await supabase
        .from("proposal_templates")
        .select(TEMPLATE_RELATION_SELECT)
        .eq("id", updatedTemplate.id)
        .single();

      if (reloadError) throw reloadError;

      return mapTemplateRecord(hydratedTemplate as TemplateWithRelations)!;
    },
    onSuccess: (data) => {
      qc.setQueryData(templateDetailKey(data.id), data);
      qc.invalidateQueries({ queryKey: templateListKey(clientId) });
    },
  });
}

export function useDeleteTemplate() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!clientId) throw new Error("No client ID");
      if (!isUuid(id)) throw new Error("Invalid template ID");

      const { error } = await supabase
        .from("proposal_templates")
        .delete()
        .eq("id", id)
        .eq("client_id", clientId);

      if (error) throw error;
    },
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: templateDetailKey(id) });
      qc.invalidateQueries({ queryKey: templateListKey(clientId) });
    },
  });
}

export function useSetDefaultTemplate() {
  const updateTemplate = useUpdateTemplate();

  return useMutation({
    mutationFn: async (id: string) => {
      return updateTemplate.mutateAsync({
        id,
        updates: { is_default: true },
      });
    },
  });
}